import Swal from "sweetalert2";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { hasText } from "../support/text";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("Dialog");
export async function confirm(title: string, text: string): Promise<boolean> {
  return <boolean>await sntConfirm(title, text);
}

/*
export function sntConfirm(title: string, text: string): Promise<boolean>;
export function sntConfirm(
  title: string,
  text: string,
  options: {
    confirmText?: string;
    cancelText?: string;
  }
): Promise<boolean>;
export function sntConfirm(
  title: string,
  text: string,
  options: {
    input?: "textarea";
    placeholder?: string | undefined;
    required?: boolean;
    confirmText?: string;
    cancelText?: string;
  }
): Promise<{ confirmed: boolean; value: string }>;
*/

export type SntDialogConfirmResult = {
  confirmed: boolean;
  denied?: boolean;
  value: string;
};

export function sntConfirm(
  title: string,
  text: string,
  options?: {
    input?: "textarea" | "text" | "select" | undefined;
    options?: any;
    label?: string | undefined;
    placeholder?: string | undefined;
    required?: boolean;
    confirmText?: string;
    cancelText?: string;
    showDenyButton?: boolean;
    denyButtonText?: string;
    customClass?: Record<string, string>;
    width?: number;
    zIndex?: number;
    inputIsEqual?: string;
  }
): Promise<boolean | SntDialogConfirmResult> {
  return new Promise((res) => {
    Swal.fire({
      title: title,
      html: text,
      icon: "question",
      showCancelButton: true,
      showConfirmButton: true,
      allowOutsideClick: false,
      showDenyButton: options?.showDenyButton,
      denyButtonText: options?.denyButtonText,
      confirmButtonText: options?.confirmText
        ? options.confirmText
        : sntTranslate(marker("Si")),
      cancelButtonText: options?.cancelText
        ? options.cancelText
        : sntTranslate(marker("No")),
      reverseButtons: true,
      input: options?.input,
      inputOptions: options?.options,
      inputLabel: options?.label,
      inputPlaceholder: options?.placeholder,
      focusCancel: false,
      focusConfirm: false,
      focusDeny: false,
      showClass: {
        backdrop: "swal2-noanimation",
        popup: "",
        icon: ""
      },
      hideClass: {
        popup: "" // disable popup fade-out animation
      },
      customClass: options?.customClass,
      toast: false,
      width: options?.width,
      didOpen: () => {
        if (options?.zIndex !== undefined) {
          Swal.getContainer()?.style.setProperty(
            "z-index",
            String(options.zIndex)
          );
        }
      },
      //position: "bottom-right",
      inputValidator: (value: string) => {
        if (options?.required && !!value) {
          return sntTranslate(marker("Valore richiesto"));
        }

        if (
          hasText(options?.inputIsEqual) &&
          value.toLowerCase() !== options?.inputIsEqual.toLowerCase()
        ) {
          return sntTranslate(marker("Il valore inserito non corrisponde"));
        }

        return null;
      }
    }).then((result) => {
      if (options?.showDenyButton) {
        res({
          confirmed: result.isConfirmed,
          denied: result.isDenied,
          value: result.value
        });
      }

      if (options && options.input) {
        res({ confirmed: result.isConfirmed, value: result.value });
      } else {
        res(result.isConfirmed);
      }
    });
  });
}

export function sntConfirmWarning(
  title: string,
  text: string
): Promise<boolean>;
export function sntConfirmWarning(
  title: string,
  text: string,
  options?: {
    confirmText?: string;
    cancelText?: string;
  }
): Promise<boolean>;
export function sntConfirmWarning(
  title: string,
  text: string,
  options?: {
    input?: "textarea" | undefined;
    placeholder?: string | undefined;
    required?: boolean;
    confirmText?: string;
    cancelText?: string;
  }
): Promise<boolean | { confirmed: boolean; value: string }> {
  return new Promise((res) => {
    Swal.fire({
      title: title,
      html: text,
      icon: "warning",
      showCancelButton: true,
      showConfirmButton: true,
      allowOutsideClick: false,
      confirmButtonText: options?.confirmText
        ? options.confirmText
        : sntTranslate(marker("Si")),
      cancelButtonText: options?.cancelText
        ? options.cancelText
        : sntTranslate(marker("No")),
      reverseButtons: true,
      input: options?.input,
      inputPlaceholder: options?.placeholder,
      showClass: {
        backdrop: "swal2-noanimation",
        popup: "",
        icon: ""
      },
      hideClass: {
        popup: "" // disable popup fade-out animation
      },
      toast: false,
      //position: "bottom-right",
      inputValidator: (value: string) => {
        if (options?.required && !!value) {
          return sntTranslate(marker("Valore richiesto"));
        }

        return null;
      }
    }).then((result) => {
      if (options && options.input) {
        res({ confirmed: result.isConfirmed, value: result.value });
      } else {
        res(result.isConfirmed);
      }
    });
  });
}

export function sntInfo(
  title: string,
  text: string,
  options?: {
    confirmText?: string;
  }
): Promise<void> {
  return new Promise((res) => {
    Swal.fire({
      title: title,
      html: text,
      icon: "info",
      showCancelButton: false,
      showConfirmButton: true,
      allowOutsideClick: false,
      confirmButtonText: options?.confirmText
        ? options.confirmText
        : sntTranslate(marker("Chiudi")),
      showClass: {
        backdrop: "swal2-noanimation",
        popup: "",
        icon: ""
      },
      hideClass: {
        popup: ""
      },
      toast: false
    }).then((result) => {
      res();
    });
  });
}

export function sntWarning(title: string, text: string): Promise<void> {
  return new Promise((res) => {
    Swal.fire({
      title: title,
      html: text,
      icon: "warning",
      showCancelButton: false,
      showConfirmButton: true,
      allowOutsideClick: false,
      confirmButtonText: sntTranslate(marker("Chiudi")),
      showClass: {
        backdrop: "swal2-noanimation",
        popup: "",
        icon: ""
      },
      hideClass: {
        popup: ""
      },
      toast: false
    }).then(() => {
      res();
    });
  });
}

export function sntAsyncTask(
  taskFn: () => Promise<void>,
  title?: string,
  text?: string | undefined
): Promise<void> {
  return new Promise((res) => {
    Swal.fire({
      title: title || sntTranslate(marker("Caricamento...")),
      html: text,
      icon: "info",
      showCancelButton: false,
      showConfirmButton: false,
      toast: true,
      position: "top-right",
      showClass: {
        backdrop: "swal2-noanimation",
        popup: "",
        icon: ""
      },
      hideClass: {
        popup: "" // disable popup fade-out animation
      },
      didOpen: async () => {
        Swal.showLoading();

        await new Promise((res) => setTimeout(res, 500));

        try {
          await taskFn();

          Swal.close();
        } catch (ex) {
          logger.error(ex);
          Swal.fire({
            title: sntTranslate(marker("Errore")),
            toast: true,
            position: "top-right",
            timer: 3000
          });
        }
      }
    });
  });
}
